// Builds the grocery list: aggregates ingredients across every planned
// recipe (see useMealPlannerData.js's groceryPlannedRecipes) into one
// heading per distinct food (ingredient identity, prep ignored), with its amount(s) listed
// beneath. Pulled out of the hook so the aggregation itself is a plain,
// testable function with no React/Supabase dependency.
import { parseQuantity } from "./utils";
import { CATEGORIES, UNIT_FAMILIES, UNIT_FAMILY_LADDERS } from "./constants";
import { normalizeIngredientName } from "./itemIdentity";

function familyForUnit(unit) {
  return UNIT_FAMILIES[unit]?.family;
}

// What makes two ingredient lines "the same food" for merging: the shared
// identity key (case, plurals, size words, parentheticals and a trailing
// ", prep" all ignored -- see itemIdentity.js), so "carrot"/"Carrots" and
// "medium yellow onion"/"yellow onion" share one heading. A name that reduces
// to nothing (e.g. only a parenthetical) falls back to its own lowercase text
// so two unrelated junk names never merge with each other. It is also the
// line's `key`, which a saved grocery mark points at (groceryState.ts).
export function identityKey(name) {
  return normalizeIngredientName(name) || name.trim().toLowerCase();
}

function toBaseQuantity(unit, qty) {
  return qty * (UNIT_FAMILIES[unit]?.toBase ?? 1);
}

function fromBaseQuantity(unit, baseQty) {
  return baseQty / UNIT_FAMILIES[unit].toBase;
}

// Largest unit in the family's ladder whose value would be >= 1, e.g. 5 tsp
// (base) picks "tbsp" (5/3 ~= 1.67), not "cup" (5/48 < 1). Falls back to the
// smallest/base unit for a total under 1 of anything larger.
function pickDisplayUnit(family, baseQty) {
  const ladder = UNIT_FAMILY_LADDERS[family];
  for (let i = ladder.length - 1; i >= 0; i--) {
    if (baseQty / UNIT_FAMILIES[ladder[i]].toBase >= 1) return ladder[i];
  }
  return ladder[0];
}

// Each placement is a recipe id (made as written) or { recipeId, factor }
// for one scaled to what it's planned for (planScaling.ts's
// groceryPlacements). The same recipe placed twice simply adds up.
export function buildGroceryList(placements, recipeById) {
  const occurrences = {};
  placements.forEach((p) => {
    const id = typeof p === "string" ? p : p.recipeId;
    const factor = typeof p === "string" ? 1 : p.factor;
    occurrences[id] = (occurrences[id] || 0) + factor;
  });

  // Pass 1: accumulate quantity per (food identity, unit-family-or-exact-unit).
  // Same-family amounts (e.g. tsp + tbsp) share a sub-entry and get summed
  // in base units; a different family/system gets its own sub-entry, so it
  // ends up as a separate amount line under the same ingredient rather than
  // being forced into the same line or duplicating the heading.
  const subMap = {};
  Object.entries(occurrences).forEach(([id, count]) => {
    const r = recipeById(id);
    if (!r) return;
    r.ingredients.forEach((ing) => {
      // "To taste" ingredients (salt, pepper, etc.) are already-on-hand
      // seasonings with no real purchase quantity -- they'd only ever show
      // up as a "0 to taste" line, so they're left off the list entirely
      // rather than shown as an amount to shop for.
      if (!ing.name.trim() || ing.type === "note" || ing.unit === "to taste") return;
      const name = ing.name.trim();
      const unit = ing.unit || "";
      const family = familyForUnit(unit);
      const subKey = `${identityKey(name)}|${family ? "fam:" + family : "unit:" + unit}`;
      if (!subMap[subKey]) {
        subMap[subKey] = { name, category: ing.category || "other", family, unit, units: new Set(), baseQuantity: 0, recipeIds: new Set() };
      }
      subMap[subKey].recipeIds.add(id);
      subMap[subKey].units.add(unit);
      const qty = parseQuantity(ing.quantity) * count;
      subMap[subKey].baseQuantity += family ? toBaseQuantity(unit, qty) : qty;
    });
  });

  // Pass 2: resolve each sub-entry's display unit/quantity (once, not per-add,
  // so there's no repeated rounding drift), then group by food identity into
  // one heading with an amounts[] list -- this is what collapses "Salt" (tsp)
  // and "Salt" (tbsp) into one heading with one combined amount, while "Milk"
  // (cup) and "Milk" (ml) become one heading with two separate amount lines.
  const itemMap = {};
  Object.values(subMap).forEach((entry) => {
    let unit = entry.unit;
    let quantity = entry.baseQuantity;
    if (entry.family === "count") {
      // Keep whatever unit the recipes used when they all agreed ("3
      // piece(s)" stays "3 piece(s)", "2 dozen" stays "2 dozen"); a mix, such
      // as "2 carrots" + "3 piece(s) carrots", is a plain count.
      unit = entry.units.size === 1 ? [...entry.units][0] : "";
      quantity = fromBaseQuantity(unit, entry.baseQuantity);
    } else if (entry.family) {
      unit = pickDisplayUnit(entry.family, entry.baseQuantity);
      quantity = fromBaseQuantity(unit, entry.baseQuantity);
    }
    const itemKey = identityKey(entry.name);
    // First sub-entry to create this heading wins name/category display
    // (so "medium carrots" + "carrots" shows under whichever recipe came first).
    if (!itemMap[itemKey]) {
      itemMap[itemKey] = { key: itemKey, name: entry.name, category: entry.category, amounts: [], recipeIds: [] };
    }
    // The recipes that need it, for "2 recipes" and its detail.
    entry.recipeIds.forEach((rid) => itemMap[itemKey].recipeIds.includes(rid) || itemMap[itemKey].recipeIds.push(rid));
    itemMap[itemKey].amounts.push({ unit, quantity });
  });

  const grouped = {};
  CATEGORIES.forEach((c) => (grouped[c] = []));
  Object.values(itemMap).forEach((item) => {
    grouped[item.category] = grouped[item.category] || [];
    grouped[item.category].push(item);
  });
  Object.keys(grouped).forEach((c) => grouped[c].sort((a, b) => a.name.localeCompare(b.name)));
  return grouped;
}
